import { X } from "lucide-react";
import { useState } from "react";

export default function MedicationModal({ isOpen, onClose, onSubmit }) {
  const [form, setForm] = useState({
    drug_name: "",
    dosage: "",
    instructions: "",
    prescribing_doctor: "",
    condition_treated: "",
    start_date: "",
    end_date: "",
    description: ""
  });
  const [reminderTimes, setReminderTimes] = useState([]);

  const handleTimeChange = (index, value) => {
    const updated = [...reminderTimes];
    updated[index] = value;
    setReminderTimes(updated);
  };

  // Add a new time input
  const addTimeInput = () => {
    setReminderTimes([...reminderTimes, '']);
  };

  // Remove a time input
  const removeTimeInput = (index) => {
    setReminderTimes(reminderTimes.filter((_, i) => i !== index));
  };
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Clean up times — remove empty or invalid entries
    const filteredTimes = reminderTimes
      .map((t) => t.trim())
      .filter((t) => t); // only keep valid non-empty strings

    const payload = {
      ...form,
      times: filteredTimes.length > 0 ? JSON.stringify(filteredTimes) : null // Only include times if there are valid entries
    }
    onSubmit(payload);
    onClose(); // Close modal after submission
    setForm({ // Reset form
      drug_name: "",
      dosage: "",
      instructions: "",
      prescribing_doctor: "",
      condition_treated: "",
      start_date: "",
      end_date: "",
      description: ""
    });

    setReminderTimes([]); // Reset reminder times
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
      <div className="relative bg-white p-6 rounded-lg shadow-lg w-full max-w-xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <h2 className="text-xl font-bold mb-6">Add Medication</h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label htmlFor="drug_name" className="text-sm font-medium  mb-1">Drug Name</label>
            <input
              type="text"
              name="drug_name"
              id="drug_name"
              value={form.drug_name}
              onChange={handleChange}
              required
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="dosage" className="text-sm font-medium mb-1">Dosage</label>
            <input
              type="text"
              name="dosage"
              id="dosage"
              placeholder="e.g. 10mg"
              value={form.dosage}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label className="mb-3 text-sm font-medium">Description (optional)</label>
            <textarea
              name="description"
              rows="2"
              value={form.description}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>

          <div className="flex flex-col">
            <label htmlFor="prescribing_doctor" className="text-sm font-medium mb-1">Prescribing Doctor</label>
            <input
              type="text"
              name="prescribing_doctor"
              id="prescribing_doctor"
              value={form.prescribing_doctor}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="condition_treated" className="text-sm font-medium mb-1">Condition Treated</label>
            <input
              type="text"
              name="condition_treated"
              id="condition_treated"
              value={form.condition_treated}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <label htmlFor="instructions" className="text-sm font-medium mb-1">Instructions (optional)</label>
            <input
              type="text"
              name="instructions"
              id="instructions"
              value={form.instructions}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="start_date" className="text-sm font-medium mb-1">Start Date</label>
            <input
              type="date"
              name="start_date"
              id="start_date"
              value={form.start_date}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="end_date" className="text-sm font-medium mb-1">End Date (optional)</label>
            <input
              type="date"
              name="end_date"
              id="end_date"
              value={form.end_date}
              onChange={handleChange}
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="mb-4">
            <label className="block font-medium text-sm mb-1">Daily Reminder Times (optional)</label>
            {reminderTimes.map((time, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(idx, e.target.value)}
                  className="border rounded px-2 py-1"
                  required
                />
                {reminderTimes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTimeInput(idx)}
                    className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    aria-label="Remove reminder time"
                  >
                    x
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addTimeInput}
              className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
            >
              + Add time
            </button>
          </div>
          <div className="flex justify-end md:col-span-2 mt-2">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
